import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { DriverLocation } from '../../../../_shared/models/drivers/maps/driver-location.model';
import { IMapInfoWindowBuilder } from '../../../../_shared/interfaces/maps/base-map-info-window-builder';
import { IMarkerFactory } from '../../../../_shared/interfaces/maps/base-marker-factory';
import { DashboardMapInfoWindowBuilder } from '../../helpers/dashboard-map-info-window-builder.helper';
import { IClusterRenderer } from '../../../../_shared/interfaces/maps/base-cluster-renderer';
import { DashboardMarkerFactory } from '../../helpers/dashboard-marker-factory.helper';
import { DashboardClusterRenderer } from '../../helpers/dashboard-cluster-renderer.helper';
import { OperationStatusDetail } from '../../models/operation-status-detail.model';
import { AccountFilterData, DateIntervalFilterData } from '../../../../_shared/models/filters/widget-filter-data.model';
import { DriverOperationsService } from '../../services/driver-operations.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { PaginationRequest } from '../../../../_shared/models/pagination-request.model';
import { CommonMapService } from '../../../../_shared/services/map/common-map.service';
import { CommonMap } from '../../../../_shared/components/common-map/common-map.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-dashboard-driver-map',
  standalone: true,
  imports: [RouterModule, CommonMap],
  templateUrl: './dashboard-driver-map.component.html',
  styleUrl: './dashboard-driver-map.component.scss',
})
export class DashboardDriverMapComponent implements OnInit {
  @Input() isLoading: boolean = false;

  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private driverOperationsService: DriverOperationsService = inject(DriverOperationsService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private mapDataService: CommonMapService = inject(CommonMapService);
  private readonly transloco = inject(TranslocoService);

  dataSubscription: Subscription;
  driverLocations: DriverLocation[] = [];
  routeLink: string = '';
  isCollapsed: boolean = false;
  clusterRenderer: IClusterRenderer = new DashboardClusterRenderer();
  markerFactory: IMarkerFactory = new DashboardMarkerFactory();
  mapInfoWindowBuilder: IMapInfoWindowBuilder = new DashboardMapInfoWindowBuilder(
    (key, params) => this.transloco.translate(key, params)
  );
  dateFilter = {} as DateIntervalFilterData;
  accountFilter = {} as AccountFilterData;
  request: PaginationRequest = {
    accountIds: this.accountFilter.accountIds,
    timeFrom: this.dateFilter.startDate!,
    timeTo: this.dateFilter.endDate!,
    page: 1,
    pageSize: 100,
  }

  private cancelSubject: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.setRouteLink();

    this.dataSubscription = this.mapDataService.data$.subscribe((data) => {
      if (!this.isCollapsed && data.length === 0) {
        this.loadDriverLocations();
      }

      this.driverLocations = data;
    });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    if (!this.isCollapsed) {
      this.mapDataService.updateData([]);
      this.dataSubscription?.unsubscribe();
    }
  }

  async loadDriverLocations(): Promise<void> {
    if (!this.accountFilter.accountIds || !this.dateFilter.startDate || !this.dateFilter.endDate) {
      return;
    }

    try {
      const response = await this.driverOperationsService.getDriversLocation(this.request, this.cancelSubject);
      const driverLocations: DriverLocation[] = response.data?.filter((driver: OperationStatusDetail) => driver.lat !== null && driver.lng !== null)
        .map((driver: OperationStatusDetail) => ({
          driverName: `${driver.firstName} ${driver.lastName}`,
          phoneNumber: driver.phoneNumber,
          vehicle: driver.assetName ?? '-',
          status: driver.status,
          lat: driver.lat ?? 0,
          lng: driver.lng ?? 0,
          heading: driver.heading,
          location: driver.location
        })
      ) as DriverLocation[] ?? [];

      this.mapDataService.updateData(driverLocations);
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  setRouteLink(): void {
    const currentPage = this.activatedRoute.snapshot.routeConfig?.path || '';
    this.isCollapsed = !currentPage.includes('map');
    this.routeLink = this.isCollapsed ? '/driver-management/dashboard/dashboard-driver-map' : '/driver-management/dashboard';
  }
}